"use client"

import type React from "react"
import {
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
    Tooltip,
    IconButton,
    useMediaQuery,
    useTheme,
} from "@mui/material"
import { IconBrandWhatsapp, IconInfoCircle } from "@tabler/icons-react"
import { type Product, getStatusText } from "../types"
import ImageCarousel from "./ImageCarousel"

interface ProductCardProps {
    product: Product
    onClick: () => void
    onInfoClick: () => void
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onClick, onInfoClick }) => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

    const handleWhatsAppClick = (e: React.MouseEvent) => {
        e.stopPropagation()

        const message = encodeURIComponent(
            `Hola, estoy interesado/a en el cuadrito "${product.name}" (ID: ${product.id}). ¿Está disponible?`,
        )

        window.open(`https://wa.me/59174318810?text=${message}`, "_blank")
    }

    const handleInfoClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        onInfoClick()
    }

    const statusText = getStatusText(product.status)
    const isLastOne = product.cantidad === 1

    const InfoRow = ({ label, value }: { label: string; value: string | number }) => (
        <Box sx={{ display: "flex", gap: 1, mb: 0.75 }}>
            <Typography
                variant="body2"
                component="span"
                sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                }}
            >
                {label}:
            </Typography>
            <Typography
                variant="body2"
                component="span"
                sx={{
                    color: "text.primary",
                    fontWeight: 400,
                }}
            >
                {value}
            </Typography>
        </Box>
    )

    return (
        <Card
            onClick={onClick}
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                cursor: "pointer",
                position: "relative",
                "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: "0 12px 20px rgba(0, 0, 0, 0.15)",
                },
            }}
        >
            {isLastOne && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        backgroundColor: "rgba(220, 53, 69, 0.9)",
                        color: "white",
                        padding: "4px 12px",
                        borderRadius: "20px",
                        fontSize: "0.65rem",
                        fontWeight: "bold",
                        zIndex: 2,
                        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                    }}
                >
                    ¡Último disponible!
                </Box>
            )}

            <ImageCarousel images={[product.imageUrls[0]]} enableFullscreen={false} />

            <CardContent
                sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: isMobile ? "8px" : 2,
                    "&:last-child": {
                        pb: isMobile ? "8px" : 2,
                    },
                }}
            >
                {isMobile ? (
                    // Vista mobile optimizada
                    <>
                        <Typography
                            variant="h6"
                            component="h3"
                            color="primary"
                            sx={{
                                mb: 0.5,
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                lineHeight: 1.2,
                                height: "2.2rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {product.name}
                        </Typography>

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 0.75,
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontWeight: "bold",
                                        fontSize: "1rem",
                                        color: "secondary.main",
                                        lineHeight: 1,
                                    }}
                                >
                                    {product.price}
                                </Typography>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: "0.75rem",
                                        color: "secondary.main",
                                        fontWeight: "bold",
                                    }}
                                >
                                    Bs.
                                </Typography>
                            </Box>

                            <Chip
                                label={statusText}
                                size="small"
                                sx={{
                                    backgroundColor: product.status === "ready" ? "#0d6336" : "#3c2a20",
                                    color: "white",
                                    fontWeight: "bold",
                                    fontSize: "0.65rem",
                                    height: "18px",
                                    "& .MuiChip-label": {
                                        px: 1,
                                    },
                                }}
                            />
                        </Box>

                        <Typography
                            variant="caption"
                            sx={{
                                color: "text.secondary",
                                mb: 0.75,
                                fontSize: "0.7rem",
                            }}
                        >
                            {product.size}
                        </Typography>

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleWhatsAppClick}
                            startIcon={<IconBrandWhatsapp size={16} />}
                            sx={{
                                backgroundColor: "#25D366",
                                "&:hover": {
                                    backgroundColor: "#128C7E",
                                },
                                fontSize: "0.75rem",
                                py: 0.5,
                                mt: "auto",
                                minHeight: 0,
                            }}
                        >
                            Consultar
                        </Button>
                    </>
                ) : (
                    // Vista desktop original
                    <>
                        <Typography
                            variant="h6"
                            component="h3"
                            color="primary"
                            sx={{
                                mb: 1,
                                fontWeight: 600,
                                fontSize: "1rem",
                                lineHeight: 1.2,
                                height: "2.4rem",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                            }}
                        >
                            {product.name}
                        </Typography>

                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                            <Typography
                                variant="h6"
                                component="span"
                                color="secondary"
                                sx={{
                                    fontWeight: "bold",
                                    fontSize: "1rem",
                                }}
                            >
                                {product.price} Bs.
                            </Typography>

                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <Chip
                                    label={statusText}
                                    size="small"
                                    sx={{
                                        backgroundColor: product.status === "ready" ? "#0d6336" : "#3c2a20",
                                        color: "white",
                                        fontWeight: "bold",
                                        fontSize: "0.7rem",
                                        height: "24px",
                                    }}
                                />
                                <Tooltip title="Información sobre estados">
                                    <IconButton size="small" onClick={handleInfoClick} sx={{ ml: 0.5, color: "#0d6336" }}>
                                        <IconInfoCircle size={16} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <InfoRow label="Tiempo de entrega" value="A coordinar" />
                        <InfoRow label="Tamaño" value={product.size} />
                        <InfoRow label="Disponibles" value={product.cantidad} />

                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleWhatsAppClick}
                            startIcon={<IconBrandWhatsapp size={20} />}
                            sx={{
                                backgroundColor: "#25D366",
                                "&:hover": {
                                    backgroundColor: "#128C7E",
                                },
                                fontSize: "0.875rem",
                                py: 1,
                                mt: "auto",
                            }}
                        >
                            Consultar
                        </Button>
                    </>
                )}
            </CardContent>
        </Card>
    )
}

export default ProductCard

